import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, ListSubheader, TextField } from '@material-ui/core';
import CustomAutoComplete from '../../common/components/CustomAutoComplete';
import { APAS, Role as RoleEnum, ROLES } from '../../common/constants';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_MANDATARIES, GET_MINIMAL_USERS } from '../../graphql/user/query';
import { formatName } from '../../utils/common';
import { CustomAutoCompleteProps } from '../../common/components/CustomAutoComplete/CustomAutoComplete';
import { useCreateCompaign } from '../../hooks/mutations/useCreateCompaign';
import CustomInputToolbar from './CustomInputToolbar';
import { GET_MINIMAL_ENSEIGNES } from '../../graphql/enseigne/query';
import { GET_MINIMAL_ENTITIES } from '../../graphql/entity/query';
import {
  Mandataries,
  Mandataries_mandataries,
  MandatariesVariables,
} from '../../graphql/user/types/Mandataries';
import { MAX_INPUT_LENGTH } from '../../constants/common';
import { useSendMessageToCompaign } from '../../hooks/mutations/useSendMessageToCompaign';
import useStyles from './styles';
import CustomTransferList from '../../common/components/CustomTransferList';
import { not, union } from '../../common/logics';
import { AutocompleteRenderGroupParams } from '@material-ui/lab/Autocomplete/Autocomplete';
import { AllEntities_allEntities_entities } from '../../graphql/entity/types/AllEntities';
import { AllEnseignes_allEnseignes_enseignes } from '../../graphql/enseigne/types/AllEnseignes';
import { SortOrder } from '../../types/graphql-global-types';
import {
  MinimalUsers,
  MinimalUsersVariables,
} from '../../graphql/user/types/MinimalUsers';
import {
  MinimalEntities,
  MinimalEntitiesVariables,
} from '../../graphql/entity/types/MinimalEntities';
import {
  MinimalEnseignes,
  MinimalEnseignesVariables,
} from '../../graphql/enseigne/types/MinimalEnseignes';

interface ICompaignForm {
  onCancel: () => void;
  onCreate: () => void;
}

interface IFilter {
  roles: string[];
  enseignes?: AllEnseignes_allEnseignes_enseignes[];
  entities?: AllEntities_allEntities_entities[];
  onlyManager?: boolean;
  mandataries?: Mandataries_mandataries[];
}

interface ISelect<T>
  extends Omit<
    CustomAutoCompleteProps<T>,
    'onChange' | 'onAutoCompleteChange'
  > {
  key: keyof IFilter;
  onClick: () => void;
}

const ALL_ACTIVITIES = [...APAS, RoleEnum.MANDATARY];

const initialUsersInput: any = {
  roles: ALL_ACTIVITIES,
};

const CompaignForm: FC<ICompaignForm> = ({ onCreate }) => {
  const classes = useStyles();

  const [title, setTitle] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [filter, setFilter] = useState<IFilter>(initialUsersInput);
  const [choices, setChoices] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const [
    fetchEnseignes,
    { data: enseignes, loading: loadingEnseignes },
  ] = useLazyQuery<MinimalEnseignes, MinimalEnseignesVariables>(
    GET_MINIMAL_ENSEIGNES,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const [
    fetchEntities,
    { data: entities, loading: loadingEntities },
  ] = useLazyQuery<MinimalEntities, MinimalEntitiesVariables>(
    GET_MINIMAL_ENTITIES,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const [
    fetchMandataries,
    { data: mandataries, loading: loadingMandataries },
  ] = useLazyQuery<Mandataries, MandatariesVariables>(GET_MANDATARIES, {
    fetchPolicy: 'cache-and-network',
  });

  const [fetchUsers, { loading: loadingChoices }] = useLazyQuery<
    MinimalUsers,
    MinimalUsersVariables
  >(GET_MINIMAL_USERS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getAllUsers?.users) setChoices(data?.getAllUsers?.users ?? []);
    },
  });

  const [createCompaign, { loading: loadingCreate }] = useCreateCompaign();

  const [
    sendMessageToCompaign,
    { loading: loadingSendMessage },
  ] = useSendMessageToCompaign();

  const isValid = useMemo<boolean>(
    () => !!(title && users?.length && !loadingCreate && !loadingSendMessage),
    [loadingCreate, loadingSendMessage, title, users],
  );

  const left = useMemo<any[]>(() => not(choices, users, 'id'), [
    choices,
    users,
  ]);

  const selects = useMemo<Array<ISelect<any>>>(
    () => [
      {
        key: 'roles',
        label: 'Activité',
        multiple: true,
        options: ALL_ACTIVITIES,
        getOptionLabel: (option: string) => ROLES[option],
      } as ISelect<string>,
      {
        key: 'enseignes',
        label: 'Enseigne',
        multiple: true,
        options: enseignes?.allEnseignes?.enseignes ?? [],
        getOptionLabel: (option: AllEnseignes_allEnseignes_enseignes) =>
          option?.name ?? '',
        getOptionSelected: (
          option: AllEnseignes_allEnseignes_enseignes,
          value: AllEnseignes_allEnseignes_enseignes,
        ) => option?.id === value?.id,
        onClick: () =>
          fetchEnseignes({
            variables: { input: { orderBy: { name: SortOrder.asc } } },
          }),
        loading: loadingEnseignes,
      } as ISelect<AllEnseignes_allEnseignes_enseignes>,
      {
        key: 'entities',
        label: 'Entité',
        multiple: true,
        options: entities?.allEntities?.entities ?? [],
        optionValue: filter?.entities ?? [],
        getOptionLabel: (option: AllEntities_allEntities_entities) =>
          option?.name ?? '',
        getOptionSelected: (
          option: AllEntities_allEntities_entities,
          value: AllEntities_allEntities_entities,
        ) => option?.id === value?.id,
        groupBy: (entity) => entity?.echelon?.name,
        onClick: () => {
          const enseignes =
            filter?.enseignes?.map((enseigne) => ({
              idCC: +(enseigne?.id ?? -1),
            })) ?? [];
          fetchEntities({
            variables: {
              input: { where: { enseignes }, orderBy: { name: SortOrder.asc } },
            },
          });
        },
        loading: loadingEntities,
      } as ISelect<any>,
      {
        key: 'onlyManager',
        label: 'Niveau de supervision',
        options: [true, false],
        getOptionLabel: (option: boolean) =>
          option ? 'Superviseurs' : 'Non superviseurs',
      } as ISelect<boolean>,
      {
        key: 'mandataries',
        label: 'Liaison avec un Expert CC',
        multiple: true,
        options: mandataries?.mandataries ?? [],
        optionValue: filter?.mandataries ?? [],
        getOptionLabel: (option: Mandataries_mandataries) =>
          formatName({
            firstName: option?.firstName,
            lastName: option?.lastName,
          }),
        getOptionSelected: (
          option: Mandataries_mandataries,
          value: Mandataries_mandataries,
        ) => option?.id === value?.id,
        onClick: () => fetchMandataries(),
        loading: loadingMandataries,
        disabled: !!filter?.roles?.includes(RoleEnum.MANDATARY),
      } as ISelect<Mandataries_mandataries | Mandataries_mandataries[]>,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enseignes,
      entities,
      loadingEnseignes,
      loadingEntities,
      loadingMandataries,
      mandataries,
      filter,
    ],
  );

  const handleSend = async () => {
    const userIds = users?.map((user: any) => user?.id) ?? [];
    if (isValid) {
      // Create a compaign
      const compaign = await createCompaign({
        variables: { input: { title, text, userIds } },
      });

      const compaignId = compaign?.data?.createCompaign?.id;

      // Send message to users related to the compaign
      if (compaignId) {
        const sent = await sendMessageToCompaign({
          variables: { input: { compaignId } },
        });

        if (sent?.data?.sendMessageToCompaign) onCreate();
      }
    }
  };

  const handleSelect = useCallback((option: any) => {
    setFilter((input) => {
      let _input = {
        ...input,
        ...option,
      };

      if (
        !!input?.roles?.includes(RoleEnum.MANDATARY) !==
        !!option?.roles?.includes(RoleEnum.MANDATARY)
      )
        _input = { ..._input, mandataries: undefined };

      if (option?.roles && !option?.roles?.length)
        _input = { ..._input, roles: ALL_ACTIVITIES };

      if (option?.enseignes) _input = { ..._input, entities: undefined };

      return _input;
    });
  }, []);

  const handleSelectGroup = (
    options: any[],
    selectedOptions: any,
    params: AutocompleteRenderGroupParams,
  ): any[] | undefined => {
    const prevValue = selectedOptions ?? [];

    if (!Array.isArray(prevValue)) return;

    const initialValues = {
      values: [],
      isAllSelected: false,
      numSelected: 0,
    };

    const { values, isAllSelected } = ((params?.children ??
      []) as any[])?.reduce<{
      values: any[];
      numSelected: number;
      isAllSelected: boolean;
    }>((acc, component: any, index: number, array: any[]) => {
      const total = array?.length ?? 0;
      const isSelected = !!component['props']['aria-selected'];
      const numSelected = acc.numSelected + (isSelected ? 1 : 0);
      const option = options[component.key];

      return {
        ...acc,
        numSelected,
        values: [...acc.values, option],
        isAllSelected: numSelected === total,
      };
    }, initialValues);

    return isAllSelected
      ? not(prevValue, values, 'id')
      : union(prevValue, values, 'id');
  };

  useEffect(() => {
    const enseigneIds =
      filter?.enseignes?.map((enseigne) => enseigne?.id) ?? [];
    const entityIds = filter?.entities?.map((entity) => entity?.id) ?? [];
    const mandataryIds =
      filter?.mandataries?.map((mandatary) => mandatary?.id) ?? [];

    fetchUsers({
      variables: {
        input: {
          autoComplete: filter?.onlyManager !== undefined,
          where: {
            roles: filter?.roles,
            isManager: !!filter?.onlyManager,
            enseigneIds,
            entityIds,
            mandataryIds,
          },
          orderBy: {
            firstName: SortOrder.asc,
          },
        },
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <>
      <Box mb={2}>
        <TextField
          required
          variant="outlined"
          autoFocus
          fullWidth
          label="Titre"
          value={title}
          onChange={(e) => {
            setTitle((e?.target?.value ?? '').substring(0, 191));
          }}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item container spacing={1}>
          {selects.map((select, index) => (
            <Grid key={`${select.key}-option-${index}`} item xs={12}>
              <CustomAutoComplete
                {...select}
                onAutoCompleteChange={(selected) =>
                  handleSelect({
                    [select.key]: selected,
                  })
                }
                onOpen={() => select?.onClick && select?.onClick()}
                onChange={() => {}}
                renderGroup={(params: AutocompleteRenderGroupParams) => [
                  <ListSubheader
                    className={classes.listSubHeader}
                    key={params?.key}
                    component="div"
                    onClick={() =>
                      handleSelect({
                        [select.key]: handleSelectGroup(
                          select?.options,
                          filter[select.key],
                          params,
                        ),
                      })
                    }>
                    {params?.group}
                  </ListSubheader>,
                  params?.children,
                ]}
              />
            </Grid>
          ))}
        </Grid>

        <CustomTransferList
          loadingLeft={loadingChoices}
          left={left}
          right={users}
          onLeft={(_: any[]) => setChoices(_)}
          onRight={(_: any[]) => setUsers(_)}
          getOptionLabel={(item) =>
            formatName({ firstName: item?.firstName, lastName: item?.lastName })
          }
        />
      </Grid>

      <CustomInputToolbar
        text={text}
        onChange={(text) => setText(text?.substring(0, MAX_INPUT_LENGTH))}
        onSend={handleSend}
        loading={loadingCreate || loadingSendMessage}
        disabled={!isValid}
      />
    </>
  );
};

export default CompaignForm;
